import * as React from "react";
import { Loader, Select, TextInput } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { ReportVariable } from "@bi-studio/shared/lib";
import { useAuth } from "@bi-studio/shared/hooks";
import { executeVariableQuery } from "@bi-studio/shared/apis";
import dayjs from "dayjs";

interface VariableInputProps {
    name: string;
    config?: ReportVariable;
    value: string;
    onChange: (value: string) => void;
    dataSourceId: string;
}

export function VariableInput({
    name,
    config,
    value,
    onChange,
    dataSourceId,
}: VariableInputProps) {
    const { token } = useAuth();
    const [options, setOptions] = React.useState<{ value: string; label: string }[]>([]);
    const [loading, setLoading] = React.useState(false);

    React.useEffect(() => {
        if (config?.type === "select" && config.selectConfig?.source === "query" && config.selectConfig.query) {
            if (!token) return;

            const fetchOptions = async () => {
                try {
                    setLoading(true);
                    const result = await executeVariableQuery(token, {
                        dataSourceId,
                        query: config.selectConfig!.query!,
                        pagination: { page: 1, pageSize: 100000 },
                    });

                    if (result.rows) {
                        const mappedOptions = result.rows.map((row) => {
                            const val = row['value'] !== undefined ? String(row['value']) : String(Object.values(row)[0]);
                            const lab = row['label'] !== undefined ? String(row['label']) : val;
                            return { value: val, label: lab };
                        });
                        setOptions(mappedOptions);
                    }
                } catch (err) {
                    console.error("Failed to fetch variable options:", err);
                } finally {
                    setLoading(false);
                }
            };

            fetchOptions();
        } else if (config?.type === "select" && config.selectConfig?.source === "static") {
            setOptions(
                config.selectConfig.staticOptions?.map((opt) => ({ value: opt, label: opt })) || []
            );
        }
    }, [config, token, dataSourceId]);

    const label = config?.label || name;

    if (config?.type === "date") {
        const dateValue = value ? dayjs(value).toDate() : null;

        return (
            <DateInput
                label={label}
                placeholder="Select date"
                value={dateValue}
                onChange={(date) => {
                    onChange(date ? dayjs(date).format('YYYY-MM-DD') : "");
                }}
                clearable
                valueFormat="YYYY-MM-DD"
                popoverProps={{ withinPortal: true, zIndex: 10000 }}
                size="sm"
            />
        );
    }


    if (config?.type === "select") {
        return (
            <Select
                label={label}
                placeholder={`Select ${label}`}
                data={options}
                value={value}
                onChange={(val) => onChange(val || "")}
                searchable
                clearable
                limit={1000}
                disabled={loading}
                rightSection={loading ? <Loader size="xs" /> : null}
                size="sm"
            />
        );
    }

    return (
        <TextInput
            label={label}
            placeholder={`Enter ${label}`}
            value={value}
            onChange={(e) => onChange(e.currentTarget.value)}
            size="sm"
        />
    );
}
